import { CXScoreLicences } from '../constants/CXScoreLicences'
import { CXS_TELSYS_MODE, CxsTelsysMode } from '../constants/CxsTelsysModes'
import { ReportingAccessLevels, RESTRICTED_LEVELS } from '../constants/Permissions'
import { CALL_CONTROL_OPTIONS_BROADWORKS, TelSysTypes } from '../constants/TelephonyServerTypes'
import { UserTypes } from '../../React/SelfProvisioning/Constants/Constants'
import { SelectOptionUtils } from './SelectOptionUtils'

/** i18n key prefix for CX Score licence labels (matches {@link CXScoreLanguage} entries). */
export const CX_SCORE_LICENCE_I18N_PREFIX: string = 'CX_Score_Licences_'

/**
 * <p>Whether a telsys is eligible for CX Score licensing.</p>
 *
 * <p>True for a real BroadWorks telsys, or a Simulator telsys whose call control options
 * are set to emulate BroadWorks ({@link CALL_CONTROL_OPTIONS_BROADWORKS}).</p>
 *
 * @param telSysType - The telsys type from {@link TelSysTypes}, as a numeric id or name.
 * @param callControlOptions - The telsys {@code CallControlOptions} field value; only relevant for Simulator.
 */
export function isCxsCompatibleTelSys(
    telSysType: Nullable<number | string>,
    callControlOptions?: Nullable<string>
): boolean {
    if (telSysType === TelSysTypes.BROADSOFT_BROADWORKS) return true

    return telSysType === TelSysTypes.SIMULATOR && callControlOptions === CALL_CONTROL_OPTIONS_BROADWORKS
}

/**
 * Resolves the CXS tier-mapping mode for a telephony server, or {@code undefined} for the legacy behaviour.
 *
 * BroadWorks (and a Simulator emulating it) take precedence and use {@link CXS_TELSYS_MODE.BROADWORKS}; an MS
 * Teams Business telsys uses {@link CXS_TELSYS_MODE.TEAMS_BUSINESS}. Any other telsys returns {@code undefined}.
 *
 * @param telSysType - The telsys type from {@link TelSysTypes}, as a numeric id or name.
 * @param callControlOptions - The telsys {@code CallControlOptions} field value; only relevant for Simulator.
 * @param isTeamsBusiness - True when the telsys is MS Teams with the Business integration type.
 * @returns The CXS tier-mapping mode, or undefined when the telsys uses the legacy behaviour.
 */
export function resolveCxsTelsysMode(
    telSysType: Nullable<number | string>,
    callControlOptions: Nullable<string>,
    isTeamsBusiness: boolean
): Definable<CxsTelsysMode> {
    if (isCxsCompatibleTelSys(telSysType, callControlOptions)) return CXS_TELSYS_MODE.BROADWORKS

    if (isTeamsBusiness) return CXS_TELSYS_MODE.TEAMS_BUSINESS

    return undefined
}

/**
 * User types (extension provisioning) that are restricted to {@link CXScoreLicences.NONE} only.
 * These types cannot hold an agent or manager CXS licence. Device-level scoring monitoring is still
 * available via the {@code CXScoreEnabled} device flag.
 */
const USER_TYPES_CXS_RESTRICTED: readonly string[] = [
    UserTypes.ESSENTIALS,
    UserTypes.PROFESSIONAL,
    UserTypes.CALL_CENTRE_AGENT
]

export type CxScoreLicenceSelectOption = {
    readonly label: string
    readonly value: CXScoreLicences
}

const CX_SCORE_LICENCE_OPTION_SOURCE: readonly CXScoreLicences[] = [
    CXScoreLicences.NONE,
    CXScoreLicences.CXS_AGENT,
    CXScoreLicences.CXS_MANAGER
]

const CXS_NONE_ONLY: readonly CXScoreLicences[] = [CXScoreLicences.NONE]

const CXS_AGENT_SET: readonly CXScoreLicences[] = [CXScoreLicences.NONE, CXScoreLicences.CXS_AGENT]

const CXS_MANAGER_SET: readonly CXScoreLicences[] = [CXScoreLicences.NONE, CXScoreLicences.CXS_MANAGER]

/**
 * CXS subscriptions mapped strictly to the Agent / Manager reporting tiers, matching the backend rule in
 * {@code CReqRespUserBase.isUnsupportedReportingAccessForCXSLicense} (CXS Agent for Call Centre Agent, CXS
 * Manager for Call Centre Supervisor). MS Teams Business relabels these tiers (Historic User = Essentials,
 * Real-Time User = Professional, Agent = Call Centre Agent, Manager = Call Centre Supervisor) but reuses the same
 * underlying values, so both the add-user reporting level strings and the users-by-extension {@link UserTypes}
 * values are keyed here.
 *
 * - Historic User / Essentials: no CXS licence.
 * - Real-Time User / Professional: no CXS licence (device monitoring only via CXScoreEnabled).
 * - Agent / Call Centre Agent: CXS Agent licence.
 * - Manager / Call Centre Supervisor: CXS Manager licence.
 *
 * Tiers absent from this map (Enterprise, None) are resolved differently per telsys - see
 * {@link teamsBusinessCxsAllowedLicences} and {@link broadworksCxsAllowedLicences}.
 */
const CXS_ALLOWED_LICENCES_BY_USER_TYPE: Readonly<Record<string, readonly CXScoreLicences[]>> = {
    [ReportingAccessLevels.ESSENTIALS]: CXS_NONE_ONLY,
    [UserTypes.ESSENTIALS]: CXS_NONE_ONLY,
    [ReportingAccessLevels.PROFESSIONAL]: CXS_NONE_ONLY,
    [UserTypes.PROFESSIONAL]: CXS_NONE_ONLY,
    [ReportingAccessLevels.CALL_CENTRE_AGENT]: CXS_AGENT_SET,
    [UserTypes.CALL_CENTRE_AGENT]: CXS_AGENT_SET,
    [ReportingAccessLevels.CALL_CENTRE_SUPERVISOR]: CXS_MANAGER_SET,
    [UserTypes.CALL_CENTRE_SUPERVISOR]: CXS_MANAGER_SET
}

/**
 * Resolves the CXS subscriptions assignable to an MS Teams Business licence.
 *
 * Returns {@code undefined} for tiers with no explicit mapping (Enterprise, None) so callers fall back to
 * the legacy unrestricted behaviour. This matches the backend, which for MS Teams permits CXS Agent / Manager on a
 * None reporting level (see {@code CReqRespUserBase.isUnsupportedReportingAccessForCXSLicense}, MS Teams case).
 *
 * @param userType - Reporting level / user type string for the user.
 * @returns The allowed CXS values, or {@code undefined} when the tier has no explicit MS Teams Business mapping.
 */
export function teamsBusinessCxsAllowedLicences(userType: string): Definable<readonly CXScoreLicences[]> {
    return CXS_ALLOWED_LICENCES_BY_USER_TYPE[userType]
}

/**
 * Resolves the CXS subscriptions assignable to a BroadWorks licence.
 *
 * Unlike MS Teams Business, BroadWorks maps the CXS licence strictly to the reporting tier: any tier without an
 * explicit mapping (Enterprise, None) allows None only, matching the backend, which requires CXS Agent to be
 * Call Centre Agent and CXS Manager to be Call Centre Supervisor exactly (see
 * {@code CReqRespUserBase.isUnsupportedReportingAccessForCXSLicense}, BroadWorks case).
 *
 * @param userType - Reporting level / user type string for the user.
 * @returns The allowed CXS values, or {@link CXS_NONE_ONLY} for any tier with no BroadWorks mapping.
 */
export function broadworksCxsAllowedLicences(userType: string): readonly CXScoreLicences[] {
    return CXS_ALLOWED_LICENCES_BY_USER_TYPE[userType] ?? CXS_NONE_ONLY
}

/**
 * Resolves the CXS subscriptions assignable for a telsys CXS tier-mapping mode.
 *
 * @param userType - Reporting level / user type string for the user.
 * @param mode - The telsys CXS tier-mapping mode, when the telsys maps CXS to the reporting tier.
 * @returns The allowed CXS values, or {@code undefined} when the tier has no mapping for the mode (or no mode
 *     applies), so callers fall back to the legacy restricted/unrestricted behaviour.
 */
function cxsAllowedLicencesForMode(userType: string, mode?: CxsTelsysMode): Definable<readonly CXScoreLicences[]> {
    if (mode === CXS_TELSYS_MODE.BROADWORKS) return broadworksCxsAllowedLicences(userType)

    if (mode === CXS_TELSYS_MODE.TEAMS_BUSINESS) return teamsBusinessCxsAllowedLicences(userType)

    return undefined
}

/**
 * Whether the user type / reporting tier is restricted to {@link CXScoreLicences.NONE} only.
 * Accepts add-user reporting level strings (e.g. {@link ReportingAccessLevels.ESSENTIALS}) or
 * users-by-extension {@link UserTypes} values (e.g. {@link UserTypes.ESSENTIALS}).
 *
 * @param userType - Current user type / reporting level string from the form.
 * @returns True when only {@link CXScoreLicences.NONE} should be offered.
 */
export function isUserTypeCxsRestricted(userType: string): boolean {
    return RESTRICTED_LEVELS.includes(userType as ReportingAccessLevels) || USER_TYPES_CXS_RESTRICTED.includes(userType)
}

/**
 * Builds CXS subscription select options: labels from i18n keys, values as enum values stored on forms/API.
 *
 * @param getLabel - Resolves an i18n key (e.g. {@link CX_SCORE_LICENCE_I18N_PREFIX} + enum value) to a label.
 * @returns Options suitable for select components; values match {@link CXScoreLicences} runtime strings.
 */
export function buildCxsLicenceOptions(getLabel: (i18nKey: string) => string): CxScoreLicenceSelectOption[] {
    return SelectOptionUtils.mapLanguageFileToSelectionOptions(
        CX_SCORE_LICENCE_OPTION_SOURCE,
        CX_SCORE_LICENCE_I18N_PREFIX,
        getLabel
    ) as CxScoreLicenceSelectOption[]
}

/**
 * Keeps only the options whose values appear in the allowed list, preserving the standard option order.
 *
 * @param options - Full CXS subscription option list.
 * @param allowed - Allowed CXS values for the current licence.
 * @returns Filtered options.
 */
function filterCxsOptionsByAllowed(
    options: readonly CxScoreLicenceSelectOption[],
    allowed: readonly CXScoreLicences[]
): CxScoreLicenceSelectOption[] {
    return options.filter((option: CxScoreLicenceSelectOption): boolean => allowed.includes(option.value))
}

/**
 * Builds CXS subscription options for the add-user details tab.
 *
 * For BroadWorks the options are limited strictly per reporting tier (see {@link broadworksCxsAllowedLicences}):
 * Call Centre Agent offers CXS Agent, Call Centre Supervisor offers CXS Manager, and every other tier (Enterprise,
 * Essentials, Professional, None) offers None only. For MS Teams Business the mapped tiers are limited per licence
 * (see {@link teamsBusinessCxsAllowedLicences}) while unmapped tiers fall back to the unrestricted list. Telephony
 * servers with no CXS tier mode keep the legacy restricted/unrestricted behaviour.
 *
 * @param userType - Reporting user type / access level string (see {@link ReportingAccessLevels}).
 * @param getLabel - i18n resolver for {@link CX_SCORE_LICENCE_I18N_PREFIX} keys.
 * @param mode - The telsys CXS tier-mapping mode, or undefined for the legacy behaviour (see {@link CxsTelsysMode}).
 * @returns Select options (values are {@link CXScoreLicences} runtime strings).
 */
export function cxsSelectOptionsForAddUser(
    userType: string,
    getLabel: (i18nKey: string) => string,
    mode?: CxsTelsysMode
): CxScoreLicenceSelectOption[] {
    const allOptions: CxScoreLicenceSelectOption[] = buildCxsLicenceOptions(getLabel)
    const allowed: Definable<readonly CXScoreLicences[]> = cxsAllowedLicencesForMode(userType, mode)

    if (allowed) return filterCxsOptionsByAllowed(allOptions, allowed)

    if (!isUserTypeCxsRestricted(userType)) return allOptions

    return filterCxsOptionsByAllowed(allOptions, CXS_NONE_ONLY)
}

/**
 * Builds CXS subscription options for a users-by-extension row.
 *
 * For BroadWorks the options are limited strictly per reporting tier (see {@link broadworksCxsAllowedLicences});
 * for MS Teams Business the mapped tiers are limited per licence (see {@link teamsBusinessCxsAllowedLicences});
 * other telephony servers keep the restricted/unrestricted behaviour.
 *
 * @param userType - Extension row user type (see {@link UserTypes}).
 * @param getLabel - i18n resolver for {@link CX_SCORE_LICENCE_I18N_PREFIX} keys.
 * @param mode - The telsys CXS tier-mapping mode, or undefined for the legacy behaviour (see {@link CxsTelsysMode}).
 * @returns Select options (values are {@link CXScoreLicences} runtime strings).
 */
export function cxsSelectOptionsForExtension(
    userType: string,
    getLabel: (i18nKey: string) => string,
    mode?: CxsTelsysMode
): CxScoreLicenceSelectOption[] {
    return cxsSelectOptionsForAddUser(userType, getLabel, mode)
}

/**
 * Ensures an existing backend CXS subscription remains visible in a filtered select option list. When the
 * current option list does not contain the loaded backend value, the missing option is appended using the
 * standard CX Score licence label so edit forms can still display that historic selection.
 *
 * @param options - Current visible options for the select.
 * @param currentValue - Existing backend CXS subscription value to preserve.
 * @param getLabel - i18n resolver for {@link CX_SCORE_LICENCE_I18N_PREFIX} keys.
 * @returns The original options, or the original options plus the missing current option.
 */
export function ensureCurrentCxsOptionVisible(
    options: readonly CxScoreLicenceSelectOption[],
    currentValue: Definable<CXScoreLicences>,
    getLabel: (i18nKey: string) => string
): CxScoreLicenceSelectOption[] {
    if (!currentValue || options.some((option: CxScoreLicenceSelectOption): boolean => option.value === currentValue)) {
        return [...options]
    }

    const currentOption: CxScoreLicenceSelectOption | undefined = buildCxsLicenceOptions(getLabel).find(
        (option: CxScoreLicenceSelectOption): boolean => option.value === currentValue
    )

    return currentOption ? [...options, currentOption] : [...options]
}

/**
 * Whether a loaded backend CXS subscription should be preserved rather than auto-normalised. Used for edit
 * flows where the existing backend user has not yet been changed locally. Once either the user type or the CXS
 * field has been edited, restricted-user normalisation may apply again.
 *
 * @param loadedValue - Existing backend CXS subscription value remembered for the edited user.
 * @param currentValue - Current form value for the CXS subscription field.
 * @param isTypeTouched - Whether the user type field has been edited locally.
 * @param isSubscriptionTouched - Whether the CXS subscription field has been edited locally.
 * @returns True when the loaded backend value should remain visible and untouched.
 */
export function shouldPreserveLoadedCxsSubscription(
    loadedValue: Definable<CXScoreLicences>,
    currentValue: CXScoreLicences,
    isTypeTouched: boolean,
    isSubscriptionTouched: boolean
): boolean {
    return !!loadedValue && loadedValue === currentValue && !isTypeTouched && !isSubscriptionTouched
}

/**
 * Maps a disallowed CXS subscription to the replacement when the user type is restricted. Restricted user
 * types may only hold {@link CXScoreLicences.NONE}; the dashboard-viewing tiers
 * {@link CXScoreLicences.CXS_AGENT} and {@link CXScoreLicences.CXS_MANAGER} both collapse to
 * {@link CXScoreLicences.NONE}. All other values are unchanged.
 *
 * @param current - Current CXS subscription value.
 * @returns Value to set on the form when the current tier is not allowed.
 */
export function cxsRestrictedReplacement(current: CXScoreLicences): CXScoreLicences {
    if (current === CXScoreLicences.CXS_AGENT || current === CXScoreLicences.CXS_MANAGER) {
        return CXScoreLicences.NONE
    }

    return current
}

/**
 * Maps a disallowed CXS subscription to the nearest allowed value within an allowed set. A disallowed dashboard
 * tier swaps to the set's dashboard licence when one exists ({@link CXScoreLicences.CXS_AGENT} <->
 * {@link CXScoreLicences.CXS_MANAGER}), otherwise it falls back to {@link CXScoreLicences.NONE}.
 *
 * @param current - Current CXS subscription value.
 * @param allowed - Allowed CXS values for the current tier.
 * @returns Value to set on the form; unchanged when the current value is still allowed.
 */
function cxsTierReplacement(current: CXScoreLicences, allowed: readonly CXScoreLicences[]): CXScoreLicences {
    if (allowed.includes(current)) return current

    if (current === CXScoreLicences.CXS_AGENT && allowed.includes(CXScoreLicences.CXS_MANAGER)) {
        return CXScoreLicences.CXS_MANAGER
    }

    if (current === CXScoreLicences.CXS_MANAGER && allowed.includes(CXScoreLicences.CXS_AGENT)) {
        return CXScoreLicences.CXS_AGENT
    }

    return CXScoreLicences.NONE
}

/**
 * Maps a disallowed CXS subscription to its replacement for the current user type. For BroadWorks the allowed
 * values come from {@link broadworksCxsAllowedLicences} and for MS Teams Business from
 * {@link teamsBusinessCxsAllowedLicences}; in both cases a disallowed value is swapped or collapsed via
 * {@link cxsTierReplacement}. MS Teams Business tiers with no explicit mapping, and all servers with no CXS tier
 * mode, use the restricted-user collapse of {@link cxsRestrictedReplacement}.
 *
 * @param current - Current CXS subscription value.
 * @param userType - Reporting level / user type string for the user.
 * @param mode - The telsys CXS tier-mapping mode, or undefined for the legacy behaviour (see {@link CxsTelsysMode}).
 * @returns Value to set on the form; unchanged when the current value is still allowed.
 */
export function cxsReplacementForUserType(
    current: CXScoreLicences,
    userType: string,
    mode?: CxsTelsysMode
): CXScoreLicences {
    const allowed: Definable<readonly CXScoreLicences[]> = cxsAllowedLicencesForMode(userType, mode)

    if (allowed) return cxsTierReplacement(current, allowed)

    if (!isUserTypeCxsRestricted(userType)) return current

    return cxsRestrictedReplacement(current)
}
